import React, { useState } from 'react';

import { Button, Text } from '@betterment/style-closet-react';
import { formatDistanceStrict, parseISO } from 'date-fns';

import IndividualTaskContainer from './IndividualTaskContainer';

import { trackClick } from '../../../features/track';
import { AccountTask, Invitation } from '../../data';
import { useAccount } from '../AccountProvider';
import { useCustomer } from '../CustomerProvider';
import InvitationCancellationModal from '../InvitationCancellationModal';

interface InviteExpirationTextProps {
  invitation: Invitation;
}

const InviteExpirationText: React.FC<React.PropsWithChildren<InviteExpirationTextProps>> = ({ invitation }) => {
  const timeLeft = formatDistanceStrict(parseISO(invitation.expiresAt), Date.now(), {
    unit: 'day',
  });
  let expirationTimeLeft = `within the next ${timeLeft}`;
  let isExpirationTextRed = false;

  if (timeLeft === '1 day') {
    isExpirationTextRed = true;
    expirationTimeLeft = 'by tomorrow';
  } else if (timeLeft === '0 days') {
    isExpirationTextRed = true;
    expirationTimeLeft = 'by 11:59pm (ET)';
  }

  const expirationText = `Make sure ${invitation.receiverFirstName} checks their email to respond to the invite `;

  return (
    <>
      <Text color="bmtGrey50">{expirationText}</Text>
      <Text color={isExpirationTextRed ? 'bmtOrange90' : 'bmtGrey50'}>{expirationTimeLeft}.</Text>
    </>
  );
};

interface IndividualInviteProps {
  invitation: Invitation;
}

const IndividualInvite: React.FC<React.PropsWithChildren<IndividualInviteProps>> = ({ invitation }) => {
  const { refetch: refetchCustomer } = useCustomer();
  const [isCancellationModalOpen, setIsCancellationModalOpen] = useState(false);

  return (
    <>
      {isCancellationModalOpen && (
        <InvitationCancellationModal
          invitation={invitation}
          onClose={() => setIsCancellationModalOpen(false)}
          refreshCustomer={refetchCustomer}
        />
      )}
      <Text as="p" fontSize="3">
        Invite sent to {invitation.receiverFirstName}
      </Text>
      <Text as="p" pb="1" color="bmtGrey50" fontSize="2">
        <InviteExpirationText invitation={invitation} />
      </Text>
      <Button
        onClick={() => {
          trackClick({ name: 'CancelJCInvite', module: 'CheckingSummary' });
          setIsCancellationModalOpen(true);
        }}
        type="button"
        variant="tertiary"
      >
        Cancel invite
      </Button>
    </>
  );
};

interface PendingSentJointAccountInvitationProps {
  task: AccountTask;
  refetchTasks(): void;
}

const PendingSentJointAccountInvitation: React.FC<React.PropsWithChildren<PendingSentJointAccountInvitationProps>> = ({
  task,
  // eslint-disable-next-line @typescript-eslint/unbound-method
  refetchTasks,
}) => {
  const { account } = useAccount();
  const invitations = account.sentInvitations;

  return (
    <>
      {invitations.map(invitation => (
        <IndividualTaskContainer key={invitation.invitationId} mb={[3, 3, 6]} {...task} onSkip={refetchTasks}>
          <IndividualInvite invitation={invitation} />
        </IndividualTaskContainer>
      ))}
    </>
  );
};

export default PendingSentJointAccountInvitation;
